<template>
  <BaseModal
    :visible="visible"
    background="transparent"
    :animate="true"
    align-mobile="bottom"
    @close="$emit('close')"
  >
    <div :class="$b('')">
      <PrimaryLineLink
        invert
        is-button
        :class="$b('close-button')"
        @click="$emit('close')"
      >
        Close
      </PrimaryLineLink>
      <div :class="$b('inner', { mobileOnly: true })">
        <swiper
          ref="carousel"
          :class="$b('items')"
          v-bind="{
            roundLengths: true,
            slidesPerView: 1.3,
            spaceBetween: 0,
            scrollbar: {
              el: '.swiper-scrollbar',
            },
            breakpoints: {
              465: {
                slidesPerView: 2.3,
              },
              630: {
                slidesPerView: 3.3,
              },
              1150: {
                slidesPerView: 5,
              },
            },
          }"
        >
          <swiper-slide
            v-for="(model, index) in filteredModelList"
            :key="model.id"
          >
            <button
              :class="
                $b('item', {
                  last: index + 1 === filteredModelList.length,
                  first: index === 0,
                })
              "
              @click="
                $emit('new-selected-model', {
                  modelName: model.model,
                  frameType: model.frameType,
                })
              "
            >
              <BaseHeading
                tag="h4"
                size="h2"
                :class="$b('heading')"
              >
                {{ model.model }}
              </BaseHeading>
              <span :class="$b('subheading')">
                {{
                  model.model.includes('Ultras') || model.model.includes('FORTY') ? '&nbsp;' : model.frameType
                }}
              </span>
              <DynamicImg
                :sku="model.imageSku"
                :view-angle="5"
                :width="338"
                :height="286"
                :scale="1.5"
                bg="transparent"
              />
              <div :class="$b('text')">
                <span :class="$b('text-label')">Best for:</span>
                <!-- eslint-disable-next-line vue/no-v-html -->
                <span v-html="addLineBreaks(model.bestFor)" />
              </div>
            </button>
          </swiper-slide>
        </swiper>
      </div>
      <div :class="$b('inner', { desktopOnly: true })">
        <button
          v-for="(model, index) in filteredModelList"
          :key="model.id"
          :class="
            $b('item', { noBorder: index + 1 === filteredModelList.length })
          "
          @click="
            $emit('new-selected-model', {
              modelName: model.model,
              frameType: model.frameType,
            })
          "
        >
          <BaseHeading
            tag="h4"
            size="h2"
            :class="$b('heading')"
          >
            {{ model.model }}
          </BaseHeading>
          <span :class="$b('subheading')">
            {{ model.frameType }}
          </span>
          <DynamicImg
            :sku="model.imageSku"
            :view-angle="5"
            :width="338"
            :height="286"
            :scale="1.5"
            bg="transparent"
          />
          <div :class="$b('text')">
            <span :class="$b('text-label')">Best for:</span>
            <!-- eslint-disable-next-line vue/no-v-html -->
            <span v-html="addLineBreaks(model.bestFor)" />
          </div>
        </button>
      </div>
    </div>
  </BaseModal>
</template>

<script>
import { Swiper, SwiperSlide } from 'swiper/vue'

export default {
  name: 'ComparisonSelectModal',

  components: {
    Swiper,
    SwiperSlide,
  },
  
  props: {
    filteredModelList: {
      type: Array,
      default: () => [],
    },
    visible: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['close', 'new-selected-model'],

  methods: {
    addLineBreaks(textString) {
      return textString.replace(',', '<br />')
    },
  },
}
</script>

<style lang="postcss">
.ComparisonSelectModal {
  @apply relative mx-auto mb-6;
  max-width: 1400px;

  @screen sm {
    @apply mb-0;
  }

  @screen xl {
    @apply px-sm;
  }
}

.ComparisonSelectModal__inner {
  @apply flex flex-row items-start
    xl:bg-white
    overflow-hidden;

  @screen xl {
    flex: 1 0 170px;
    border-radius: 10px;
  }

  &--desktop-only {
    @apply xlMax:hidden;
  }

  &--mobile-only {
    @apply xl:hidden;
  }

  .ComparisonSelectModal__items {
    --carousel-gutter: var(--outer-gutter);
    padding-left: var(--carousel-gutter);
    padding-right: var(--carousel-gutter);

    @screen xxl {
      --carousel-gutter: calc(
        ((100vw - theme('screens.xxl')) / 2) + var(--outer-gutter)
      );
    }
  }
}

.ComparisonSelectModal__item {
  @apply bg-white border-r border-solid border-grey-light
    text-left;

  padding: calc(theme('spacing.xs') * 2);

  @screen lg {
    padding: calc(theme('spacing.xs') + theme('spacing.sm'));
  }

  &--first {
    border-top-left-radius: 10px;
    border-bottom-left-radius: 10px;
  }

  &--last {
    @apply border-transparent;
    border-top-right-radius: 10px;
    border-bottom-right-radius: 10px;
  }

  @media (hover: hover) {
    &:hover {
      @apply bg-grey-light;
    }
  }
}

.ComparisonSelectModal__close-button {
  @apply absolute pb-0.5 mb-xs bottom-full;
  right: var(--outer-gutter);

  @screen xl {
    @apply right-sm;
  }
}

.ComparisonSelectModal__subheading {
  @apply text-sm font-normal md:text-base
    inline-block
    pb-xs;
}

.ComparisonSelectModal__text {
  @apply text-xxs md:text-xs text-mid-grey font-normal tracking-wide leading-normal
    pt-sm;
}

.ComparisonSelectModal__text-label {
  @apply text-grey tracking-wide leading-normal font-medium
    block;
  font-size: 10px;
}

.ComparisonSelectModal__items {
  @apply w-full;

  &:not(.swiper-initialized) .swiper-wrapper {
    @apply space-x-sm;
  }

  .swiper-wrapper {
    @apply flex
      flex-row;
  }

  .swiper-scrollbar {
    @apply mt-xxs mx-auto
      relative left-0;
    border-radius: 18px;
    touch-action: none;
    -ms-touch-action: none;
    background-color: transparent;
    height: 5px;
    max-width: 1500px;
    width: 100%;
  }

  .swiper-scrollbar-drag {
    @apply bg-grey transition-colors;

    height: 100%;
    width: 100%;
    position: relative;
    border-radius: 10px;
    left: 0;
    top: 0;

    &:hover {
      @apply bg-off-black bg-opacity-30;
    }
  }

  .swiper-scrollbar-cursor-drag {
    cursor: move;
  }

  .swiper-scrollbar-lock {
    display: none;
  }
}
</style>
